/**
 * Script Browser API
 *
 * Helper functions for opening scripts (.sql and .ipynb) from the script browser panel.
 */

import { useWorkspaceStore } from '../../workspace/core/store/workspaceStore';
import { isQueryTab, isNotebookTab } from '../../workspace/core/types/workspaceTypes';
import type { TabConnectionInfo } from '../../workspace/core/types/workspaceTypes';
import { openSqlFile } from '../../workspace/query-editor/services/queryFilesApi';
import {
  getFileNameFromPath,
  hashText,
} from '../../workspace/query-editor/utils/queryFileUtils';
import { openNotebookFile } from '../../workspace/notebook/services/notebookFileApi';
import { deserializeFromIpynb } from '../../workspace/notebook/utils/ipynbDeserializer';
import { computeNotebookHash } from '../../workspace/notebook/utils/ipynbSerializer';
import { useScriptBrowserStore } from '../store/scriptBrowserStore';
import { useConnectionStore } from '../../connections/store/connectionStore';
import { useContextSelectionStore } from '../../toolbar-context';
import { useDatabaseUIStore } from '../../database-objects/store/uiStore';
import { safeConnectionName, safeConnectionServer } from '../../../types/safeConnectionDisplay';

/** Normalize file path for comparison (forward slashes, trim). */
const normalizePath = (p: string): string => p.replace(/\\/g, '/').trim();

/** Check if a file path is a notebook (.ipynb). */
const isNotebookPath = (p: string): boolean => p.toLowerCase().endsWith('.ipynb');

/**
 * Open a script file in the appropriate tab type (.sql → query, .ipynb → notebook).
 * If a tab with the same filePath is already open, activates it instead.
 * If the script has a connection association and that connection is active,
 * auto-applies it to the new tab.
 *
 * Returns the tab ID, 'file-not-found', or null on failure.
 */
export async function openScriptInTab(
  filePath: string
): Promise<string | 'file-not-found' | null> {
  const normalized = normalizePath(filePath);

  if (isNotebookPath(normalized)) {
    return openNotebookInTab(filePath, normalized);
  }

  return openSqlInTab(filePath, normalized);
}

/** Open a .sql file in a query tab. */
async function openSqlInTab(
  filePath: string,
  normalized: string
): Promise<string | 'file-not-found' | null> {
  const { tabs, setActiveTab, addTab } = useWorkspaceStore.getState();

  // Check if file is already open in a tab
  const existingTab = tabs.find(
    (tab) =>
      isQueryTab(tab) &&
      normalizePath(tab.content.filePath ?? '') === normalized
  );

  if (existingTab) {
    setActiveTab(existingTab.id);
    return existingTab.id;
  }

  // Read the file via IPC
  const result = await openSqlFile({ filePath });

  if (!result.success) {
    const errorMsg = (result.error ?? '').toLowerCase();
    if (errorMsg.includes('enoent') || errorMsg.includes('no such file')) {
      return 'file-not-found';
    }
    return null;
  }

  const query = result.query ?? '';
  const connectionInfo = await resolveConnectionInfoForTab(normalized);

  const tabId = addTab({
    type: 'query',
    title: getFileNameFromPath(result.filePath ?? filePath),
    content: {
      query,
      filePath: result.filePath ?? filePath,
      fileLastSavedHash: hashText(query),
    },
    ...(connectionInfo ? { connectionInfo } : {}),
  });

  useScriptBrowserStore
    .getState()
    .recordScriptOpened(result.filePath ?? filePath, query.slice(0, 100));

  return tabId;
}

/** Open a .ipynb file in a notebook tab. */
async function openNotebookInTab(
  filePath: string,
  normalized: string
): Promise<string | 'file-not-found' | null> {
  const { tabs, setActiveTab, addTab } = useWorkspaceStore.getState();

  // Check if notebook is already open in a tab
  const existingTab = tabs.find(
    (tab) =>
      isNotebookTab(tab) &&
      normalizePath(tab.content.filePath ?? '') === normalized
  );

  if (existingTab) {
    setActiveTab(existingTab.id);
    return existingTab.id;
  }

  // Read the file via IPC
  const result = await openNotebookFile({ filePath });

  if (!result.success) {
    const errorMsg = (result.error ?? '').toLowerCase();
    if (errorMsg.includes('enoent') || errorMsg.includes('no such file')) {
      return 'file-not-found';
    }
    return null;
  }

  try {
    const { content } = deserializeFromIpynb(result.content);
    const fileName = getFileNameFromPath(result.filePath);
    const hash = computeNotebookHash(content);

    const tabId = addTab({
      title: fileName,
      type: 'notebook',
      content: {
        ...content,
        filePath: result.filePath,
        lastSavedHash: hash,
      },
    });

    // Generate preview: cell count summary
    const codeCells = content.cells.filter((c) => c.cellType === 'code').length;
    const mdCells = content.cells.filter((c) => c.cellType === 'markdown').length;
    const preview = `${codeCells} code cell${codeCells !== 1 ? 's' : ''}, ${mdCells} markdown cell${mdCells !== 1 ? 's' : ''}`;

    useScriptBrowserStore
      .getState()
      .recordScriptOpened(result.filePath, preview);

    return tabId;
  } catch {
    return null;
  }
}

/**
 * Resolve connection info for a script file path.
 *
 * Priority:
 * 1) Script's saved connection association (if active)
 * 2) Current Explorer selection (prompt user if needed)
 */
async function resolveConnectionInfoForTab(
  normalizedPath: string
): Promise<TabConnectionInfo | null> {
  const associated = resolveAssociatedConnectionForScript(normalizedPath);
  if (associated) {
    return applySelectedDatabaseIfMatches(associated);
  }

  // Fallback: use current Explorer connection context (prompts if missing)
  const context = await useContextSelectionStore
    .getState()
    .requestContext({ requireConnection: true, requireDatabase: false }, 'Open Script');

  if (!context) return null;

  return applySelectedDatabaseIfMatches({
    connectionId: context.connectionId,
    connectionName: context.connectionName,
    serverHost: context.serverHost,
  });
}

/**
 * Resolve an associated connection for a script file path.
 * Returns TabConnectionInfo if the script has an active associated connection.
 */
function resolveAssociatedConnectionForScript(
  normalizedPath: string
): TabConnectionInfo | null {
  const store = useScriptBrowserStore.getState();
  const script = store.scripts.find(
    (s) => normalizePath(s.filePath) === normalizedPath
  );

  if (!script?.connectionId) return null;

  const { activeConnections } = useConnectionStore.getState();
  const active = activeConnections.find(
    (ac) => ac.id === script.connectionId && ac.status === 'connected'
  );

  if (!active) return null;

  return {
    connectionId: active.id,
    connectionName: safeConnectionName(active.connection),
    serverHost: safeConnectionServer(active.connection),
  };
}

function applySelectedDatabaseIfMatches(
  connectionInfo: TabConnectionInfo
): TabConnectionInfo {
  const { selectedDatabase, selectedDatabaseConnectionId } = useDatabaseUIStore.getState();
  if (
    connectionInfo.connectionId &&
    selectedDatabase &&
    selectedDatabaseConnectionId === connectionInfo.connectionId
  ) {
    return { ...connectionInfo, database: selectedDatabase };
  }

  return connectionInfo;
}

/**
 * Check if a file path is currently open in a query or notebook tab.
 */
export function isScriptOpenInTab(filePath: string): boolean {
  const normalized = normalizePath(filePath);
  const { tabs } = useWorkspaceStore.getState();
  return tabs.some((tab) => {
    if (isQueryTab(tab)) {
      return normalizePath(tab.content.filePath ?? '') === normalized;
    }
    if (isNotebookTab(tab)) {
      return normalizePath(tab.content.filePath ?? '') === normalized;
    }
    return false;
  });
}
